// „Lecții suplimentare” (cererea Mihaelei, 19.10): elevii cu lecții suplimentare pe luni, ca grupele.
// Serverul (GET /extra-lessons) dă luna fiecărui elev: ai noștri — luna lecției a 31-a, din alte școli — luna
// primei lecții la noi. Aici: filtrul, căutarea, gruparea pe luni și textele.

const MONTHS = [
   "Ianuarie",
   "Februarie",
   "Martie",
   "Aprilie",
   "Mai",
   "Iunie",
   "Iulie",
   "August",
   "Septembrie",
   "Octombrie",
   "Noiembrie",
   "Decembrie",
];

export const KINDS = [
   { value: "all", label: "Toți" },
   { value: "ours", label: "Ai noștri" },
   { value: "other", label: "Din alte școli" },
];

const monthIndex = (month) => Number(String(month || "").slice(5, 7)) - 1;

/** „2026-10” → „Octombrie 2026”. */
export function monthLabel(month) {
   const i = monthIndex(month);
   const year = String(month || "").slice(0, 4);
   return MONTHS[i] ? `${MONTHS[i]} ${year}` : String(month || "—");
}

/** „2026-10” → „OCT” (pentru avatarul lunii). */
export function monthShort(month) {
   const i = monthIndex(month);
   return MONTHS[i] ? MONTHS[i].slice(0, 3).toUpperCase() : "—";
}

/** Culoarea avatarului lunii: aceeași lună — aceeași culoare. */
export function monthColor(month) {
   const i = Math.max(0, monthIndex(month));
   return `hsl(${(i * 30 + 10) % 360} 95% 86%)`;
}

export const fullName = (s) => `${s?.firstName || ""} ${s?.lastName || ""}`.trim();

/** Orele lecțiilor sunt „de perete” scrise ca UTC — se afișează ca atare: „22.10 10:00”. */
export function lessonTime(iso) {
   if (!iso) return "";
   const d = new Date(iso);
   if (Number.isNaN(d.getTime())) return "";
   const p = (n) => String(n).padStart(2, "0");
   return `${p(d.getUTCDate())}.${p(d.getUTCMonth() + 1)} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
}

export function matchesKind(student, kind) {
   if (kind === "ours") return !student?.fromOtherSchool;
   if (kind === "other") return !!student?.fromOtherSchool;
   return true;
}

export function matchesQuery(student, query) {
   const q = String(query || "").trim().toLowerCase();
   if (!q) return true;
   return [student?.firstName, student?.lastName, fullName(student), student?.phone, student?.group]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(q);
}

/**
 * Lunile (cele noi sus), fiecare cu elevii ei (după nume) și numărători. Doar elevii care trec de filtru și de căutare;
 * lunile fără niciun elev rămas nu apar.
 */
export function groupByMonth(students, { kind = "all", query = "" } = {}) {
   const byMonth = new Map();
   for (const s of students || []) {
      if (!s?.month || !matchesKind(s, kind) || !matchesQuery(s, query)) continue;
      if (!byMonth.has(s.month)) byMonth.set(s.month, []);
      byMonth.get(s.month).push(s);
   }
   return [...byMonth.entries()]
      .sort((a, b) => (a[0] < b[0] ? 1 : a[0] > b[0] ? -1 : 0))
      .map(([month, list]) => {
         const sorted = [...list].sort((a, b) => fullName(a).localeCompare(fullName(b), "ro") || a.id - b.id);
         return {
            month,
            label: monthLabel(month),
            students: sorted,
            count: sorted.length,
            ours: sorted.filter((s) => !s.fromOtherSchool).length,
            other: sorted.filter((s) => s.fromOtherSchool).length,
            withNext: sorted.filter((s) => s.nextLesson).length,
            extraDone: sorted.reduce((n, s) => n + (Number(s.extraDone) || 0), 0),
         };
      });
}

const CALL_RESULT = { CONTINUA: "Continuă", NU_CONTINUA: "Nu continuă", NU_RASPUNDE: "Nu răspunde" };

/** Starea din rând: lecția următoare (verde) sau „Nimic programat” (oranj). */
export function rowStatus(student) {
   if (student?.nextLesson) return { text: `Următoarea ${lessonTime(student.nextLesson)}`, tone: "booked" };
   return { text: "Nimic programat", tone: "pending" };
}

/** Rândul al doilea din dreapta: cine e și ultimul apel din „De sunat”. */
export function rowKind(student) {
   const kind = student?.fromOtherSchool ? "Din altă școală" : "Al nostru";
   const call = student?.lastCall ? ` · apel: ${CALL_RESULT[student.lastCall.result] || student.lastCall.result}` : "";
   return `${kind}${call}`;
}

/** Tot ce știm despre elev, pentru titlul rândului (la trecerea cu mouse-ul). */
export function rowTitle(student) {
   const parts = [
      student?.fromOtherSchool ? "Din altă școală" : `Al nostru, ${student?.lessonsTotal ?? "?"} lecții în total`,
      student?.group ? `grupa ${student.group}` : "",
      `prima lecție suplimentară: ${lessonTime(student?.firstExtra) || "—"}`,
      `suplimentare: ${student?.extraDone ?? 0} făcute, ${student?.extraPlanned ?? 0} programate`,
      student?.lastLesson ? `ultima: ${lessonTime(student.lastLesson)}` : "",
      student?.lastCall
         ? `ultimul apel: ${CALL_RESULT[student.lastCall.result] || student.lastCall.result}${student.lastCall.note ? ` — ${student.lastCall.note}` : ""}${student.lastCall.doneBy ? ` (${student.lastCall.doneBy})` : ""}`
         : "",
   ];
   return parts.filter(Boolean).join(" · ");
}
